import { useCallback, useEffect, useMemo, useState } from 'react';

import { LIGHT, NIGHT, type Palette } from '@/constants/theme';

export type ThemePref = 'light' | 'night' | 'auto';

const STORAGE_KEY = 'voicenotes.theme';

function systemNight(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'night' || v === 'auto') return v;
  } catch {
    /* private mode */
  }
  return 'auto';
}

export function useTheme() {
  const [pref, setPrefState] = useState<ThemePref>(readPref);
  const [night, setNight] = useState(() => {
    const p = readPref();
    return p === 'night' || (p === 'auto' && systemNight());
  });

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const sync = () => {
      if (pref === 'auto') setNight(mq.matches);
      else setNight(pref === 'night');
    };
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [pref]);

  const colors: Palette = night ? NIGHT : LIGHT;

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--color-bg', colors.bg);
    root.style.setProperty('--color-surface', colors.surface);
    root.style.setProperty('--color-ink', colors.ink);
    root.style.setProperty('--color-sub', colors.sub);
    root.style.setProperty('--color-border', colors.border);
    root.style.setProperty('--color-hivis', colors.hivis);
    root.style.setProperty('--color-chalk', colors.chalk);
    root.style.setProperty('--color-green', colors.green);
    root.style.setProperty('--color-tape', colors.tape);
    root.style.setProperty('--color-ghost', colors.ghost);
    root.style.setProperty('--color-chalkTint', colors.chalkTint);
    root.style.setProperty('--color-hivisTint', colors.hivisTint);
    root.style.setProperty('--font-body', '"Barlow", system-ui, sans-serif');
    root.style.setProperty('--font-cond', '"Barlow Condensed", system-ui, sans-serif');
    root.style.setProperty('--font-mono', '"IBM Plex Mono", ui-monospace, monospace');
    document.body.style.background = colors.bg;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', colors.hivis);
  }, [colors]);

  const setPref = useCallback((next: ThemePref) => {
    setPrefState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    setNight(next === 'night' || (next === 'auto' && systemNight()));
  }, []);

  return useMemo(() => ({ pref, setPref, night, colors }), [pref, setPref, night, colors]);
}
